import React from 'react'
import {Typography, Divider, Button} from 'antd'
import {ClockCircleOutlined} from '@ant-design/icons'
import {observer} from 'mobx-react'
import {useInjection} from 'dna-react-ioc'
import classNames from 'classnames'
import {useTranslation} from 'react-i18next'

import {useScreenType} from '@/hooks'
import {Status} from '@/components'
import {TagType} from '@/components/dumb/Tag/props'

import {IApplicationProgressStore} from '../../IApplicationProgressStore'
import {ApplicationProgressStoreSymbol} from '../../ApplicationProgressPage'
import {AdditionalInfoModal} from '../AdditionalInfoModal'
import {ReviewAndSignModal} from '../ReviewAndSignModal'

import {ApplicationProgressStep, OnboardingProcessStatusEnum, Step} from './types'
import {ApplicationProgressProps} from './props'
import styles from './ApplicationProgress.scss'

const {Text, Title} = Typography

export const ApplicationProgress: React.FC<ApplicationProgressProps> = observer(() => {
    const {t} = useTranslation()
    const store = useInjection<IApplicationProgressStore>(ApplicationProgressStoreSymbol)
    const {isMobile, isTablet} = useScreenType()

    const renderStepStatus = (step: Step) => {
        switch (step.applicationStatus) {
            case OnboardingProcessStatusEnum.COMPLETED:
                return (
                    <Status
                        status={t('ApplicationProgress.status.complete')}
                        type={TagType.Success}
                        iconType='tick'
                    />
                )

            case OnboardingProcessStatusEnum.DOCUSIGN_IN_PROGRESS:
                return (
                    <div className={styles.stepStatusSection}>
                        <Status
                            status={t('ApplicationProgress.status.signingContract')}
                            type={TagType.Processing}
                            iconType='exclamationMark'
                        />
                        <Button
                            type='link'
                            size='small'
                            onClick={() => store.resendContractEmail()}
                            className={styles.resendButton}
                            loading={store.isResendingEmail}
                            disabled={store.isResendingEmail}
                        >
                            {t('ApplicationProgress.resendContractEmail')}
                        </Button>
                    </div>
                )

            case OnboardingProcessStatusEnum.REVIEW_AND_SIGN_IN_PROGRESS:
            case OnboardingProcessStatusEnum.IN_PROGRESS:
                return (
                    <Status
                        status={t('ApplicationProgress.status.underReview')}
                        type={TagType.Default}
                        iconType='clock'
                    />
                )

            case OnboardingProcessStatusEnum.REVIEW_AND_SIGN_NEEDED:
                return (
                    <Button
                        type='primary'
                        onClick={() => store.openReviewAndSignModal()}
                        className={styles.reviewAndSignButton}
                    >
                        {t('ApplicationProgress.status.reviewAndSign')}
                    </Button>
                )

            case OnboardingProcessStatusEnum.NEED_ADDITIONAL_INFO:
                return (
                    <Status
                        status={t('ApplicationProgress.status.actionRequired')}
                        type={TagType.Processing}
                        iconType='exclamationMark'
                        clickable={true}
                        onClick={() => store.openAdditionalInfoModal()}
                    />
                )

            default:
                return null
        }
    }

    const contractSigningStatus = store.applicationStatus.contractSigning
    const isReviewAndSignStatus =
        contractSigningStatus === OnboardingProcessStatusEnum.REVIEW_AND_SIGN_NEEDED ||
        contractSigningStatus === OnboardingProcessStatusEnum.REVIEW_AND_SIGN_IN_PROGRESS

    const steps: Step[] = [
        {
            number: ApplicationProgressStep.APPLICATION_SUBMITTED,
            title: t('ApplicationProgress.steps.application.title'),
            description: t('ApplicationProgress.steps.application.description'),
            status:
                store.applicationStatus.applicationSubmission ===
                OnboardingProcessStatusEnum.COMPLETED
                    ? 'finish'
                    : 'wait',
            completed:
                store.applicationStatus.applicationSubmission ===
                OnboardingProcessStatusEnum.COMPLETED,
            applicationStatus: store.applicationStatus.applicationSubmission
        },
        {
            number: ApplicationProgressStep.CONTRACT_PREPARATION,
            title: t('ApplicationProgress.steps.contractPreparation.title'),
            description: t('ApplicationProgress.steps.contractPreparation.description'),
            status:
                store.applicationStatus.contractPreparation ===
                OnboardingProcessStatusEnum.IN_PROGRESS
                    ? 'process'
                    : store.applicationStatus.contractPreparation ===
                        OnboardingProcessStatusEnum.COMPLETED
                      ? 'finish'
                      : 'wait',
            completed:
                store.applicationStatus.contractPreparation ===
                OnboardingProcessStatusEnum.COMPLETED,
            applicationStatus: store.applicationStatus.contractPreparation,
            icon:
                store.applicationStatus.contractPreparation ===
                OnboardingProcessStatusEnum.IN_PROGRESS ? (
                    <ClockCircleOutlined style={{color: 'var(--color-blue2)'}} />
                ) : undefined
        },
        {
            number: ApplicationProgressStep.CONTRACT_SIGNING,
            title: t('ApplicationProgress.steps.contractSigning.title'),
            description: isReviewAndSignStatus
                ? t('ApplicationProgress.steps.contractSigning.reviewAndSignDescription')
                : t('ApplicationProgress.steps.contractSigning.description'),
            status:
                contractSigningStatus === OnboardingProcessStatusEnum.IN_PROGRESS ||
                contractSigningStatus === OnboardingProcessStatusEnum.REVIEW_AND_SIGN_NEEDED ||
                contractSigningStatus === OnboardingProcessStatusEnum.REVIEW_AND_SIGN_IN_PROGRESS ||
                contractSigningStatus === OnboardingProcessStatusEnum.DOCUSIGN_IN_PROGRESS
                    ? 'process'
                    : contractSigningStatus === OnboardingProcessStatusEnum.COMPLETED
                      ? 'finish'
                      : 'wait',
            completed: contractSigningStatus === OnboardingProcessStatusEnum.COMPLETED,
            applicationStatus: contractSigningStatus
        },
        {
            number: ApplicationProgressStep.UNDERWRITING,
            title: t('ApplicationProgress.steps.finalReview.title'),
            description: t('ApplicationProgress.steps.finalReview.description'),
            status:
                store.applicationStatus.underwriting === OnboardingProcessStatusEnum.IN_PROGRESS
                    ? 'process'
                    : store.applicationStatus.underwriting === OnboardingProcessStatusEnum.COMPLETED
                      ? 'finish'
                      : 'wait',
            completed:
                store.applicationStatus.underwriting === OnboardingProcessStatusEnum.COMPLETED,
            applicationStatus: store.applicationStatus.underwriting
        },
        {
            number: ApplicationProgressStep.ACCEPT_PAYMENTS,
            title: t('ApplicationProgress.steps.acceptPayments.title'),
            description: t('ApplicationProgress.steps.acceptPayments.description'),
            status:
                store.applicationStatus.acceptPayments === OnboardingProcessStatusEnum.IN_PROGRESS
                    ? 'process'
                    : store.applicationStatus.acceptPayments ===
                        OnboardingProcessStatusEnum.COMPLETED
                      ? 'finish'
                      : 'wait',
            completed:
                store.applicationStatus.acceptPayments === OnboardingProcessStatusEnum.COMPLETED,
            applicationStatus: store.applicationStatus.acceptPayments
        }
    ]

    const isSmallScreen = isMobile || isTablet

    return (
        <div
            className={classNames(styles.progressContainer, {
                [styles.progressContainerMobile]: isSmallScreen
            })}
        >
            <div
                className={classNames(styles.content, {
                    [styles.contentMobile]: isSmallScreen
                })}
            >
                <div
                    className={classNames(styles.leftColumn, {[styles.leftColumnMobile]: isMobile})}
                >
                    <div className={styles.header}>
                        <Text type='secondary' className={styles.subtitle}>
                            {t('ApplicationProgress.trackProgress')}
                        </Text>
                    </div>

                    <Divider />

                    <div className={styles.stepsList}>
                        {steps.map((step, index) => (
                            <div
                                key={index}
                                className={classNames(styles.stepItemWrapper, {
                                    [styles.stepItemWrapperMobile]: isSmallScreen
                                })}
                            >
                                <div
                                    className={classNames(styles.stepItem, {
                                        [styles.stepItemMobile]: isSmallScreen
                                    })}
                                >
                                    <div className={styles.stepLeftContent}>
                                        <div
                                            className={classNames(styles.stepNumberContainer, {
                                                [styles.stepNumberContainerMobile]: isSmallScreen
                                            })}
                                        >
                                            <div
                                                className={classNames(styles.stepNumber, {
                                                    [styles.stepNumberMobile]: isSmallScreen
                                                })}
                                            >
                                                {step.number}
                                            </div>
                                        </div>
                                        <div className={styles.stepContent}>
                                            <div className={styles.stepHeader}>
                                                <Title
                                                    level={4}
                                                    className={classNames(styles.stepTitle, {
                                                        [styles.stepTitleMobile]: isSmallScreen
                                                    })}
                                                >
                                                    {step.title}
                                                </Title>
                                            </div>
                                            <Text
                                                type='secondary'
                                                className={classNames(styles.stepDescription, {
                                                    [styles.stepDescriptionMobile]: isSmallScreen
                                                })}
                                            >
                                                {step.description}
                                            </Text>
                                        </div>
                                    </div>
                                    {renderStepStatus(step)}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {!isSmallScreen && (
                    <div
                        className={classNames(styles.rightColumn, {
                            [styles.rightColumnMobile]: isSmallScreen
                        })}
                    >
                        <div
                            className={classNames(styles.illustration, {
                                [styles.illustrationMobile]: isSmallScreen
                            })}
                        >
                            <img
                                src={require('~/assets/img/signUp/onboarding_review.svg')}
                                alt='Application Progress'
                                className={styles.illustrationImage}
                            />
                        </div>
                    </div>
                )}
            </div>

            <AdditionalInfoModal
                open={store.isAdditionalInfoModalOpen}
                onCancel={() => store.closeAdditionalInfoModal()}
                revertReasons={store.additionalInfo?.revertReasons || []}
                processKey={store.processKey}
                applicationProgressStore={store}
            />

            <ReviewAndSignModal
                open={store.isReviewAndSignModalOpen}
                onCancel={() => store.closeReviewAndSignModal()}
                onDownload={() => store.downloadMsa()}
                onSign={() => store.signContract()}
                isPreviewLoading={store.isPreviewingMsa}
                isPreviewReady={!!store.msaBlobUrl}
                isSigningContract={store.isSigningContract}
            />
        </div>
    )
})
